import { Toaster } from "ui-widgets";
import SearchOptions from "datalign-module-template/models/search-options";
import Boundary from "models/geospatial/boundary";
import Point from "models/geospatial/point";
import Asset from "datalign-module-template/models/asset";
import AuthService from "./AuthService";
import FacetFilter from "models/facet-filter";
import RelatedInput from "models/input/related-input";
import Template from "models/template";
import { UUID } from "datalign-module-template/models/uuid";

export default class SolrService {
  public readonly solrService = {
    facets: async <T>() => {
      const r = await fetch(`${process.env.REACT_APP_API_HOST}/facets`, {
        method: "GET",
        headers: AuthService.siteletHeaders,
        credentials: "include",
      });
      if (r.ok) {
        const resp = await r.json();

        if (resp?.errors) {
          Toaster.makeToast("Error on query", "error");
        }
        return resp?.results as T;
      } else if (r.status === 401) {
        this.UserReset();
      }
    },
    facet_image: async (
      facet_name: string,
      facet_field_name: string,
      signal: AbortSignal,
    ) => {
      const params = new URLSearchParams({
        facet_name,
        facet_field_name,
      }).toString();
      const r = await fetch(
        `${process.env.REACT_APP_API_HOST}/facets/image?${params}`,
        {
          method: "GET",
          headers: AuthService.siteletHeaders,
          credentials: "include",
          signal,
        },
      );
      if (!r.ok) throw new Error("Failed to fetch image");
      const blob = await r.blob();
      return URL.createObjectURL(blob);
    },
    get: async <T>(id: string) => {
      const params = new URLSearchParams({
        id,
      }).toString();
      const r = await fetch(
        `${process.env.REACT_APP_API_HOST}/inputs?${params}`,
        {
          method: "GET",
          headers: AuthService.siteletHeaders,
          credentials: "include",
        },
      );
      if (r.ok) {
        const resp = await r.json();

        if (resp?.errors) {
          Toaster.makeToast("Error on query", "error");
        }
        return resp?.results.data[0] as T;
      }
      return this.UserReset();
    },
    list: async <T>(
      signal: AbortSignal,
      search_options?: SearchOptions,
      facets?: [string, string][],
      get_facets?: boolean,
    ): Promise<{
      count: number;
      data: T[];
      facets?: Record<string, FacetFilter[]>;
    }> => {
      const params = new URLSearchParams({
        search_term: search_options?.search_term ?? "",
        page_num: search_options?.page_num.toString() ?? "",
        page_rows: search_options?.page_rows.toString() ?? "",
        order_by_field: search_options?.order_by?.column_name ?? "",
        order_by_order: search_options?.order_by?.order ?? "",
        facet_fields: facets
          ? JSON.stringify(
              facets
                .filter(
                  (facet) =>
                    ![
                      "order_by",
                      "search_term",
                      "order_dir",
                      "page",
                      "rows",
                    ].includes(facet[0]),
                )
                .map((facet) => ({
                  field: facet[0],
                  value: facet[1],
                })),
            )
          : "",
        get_facets: get_facets ? "true" : "false",
      }).toString();
      const r = await fetch(
        `${process.env.REACT_APP_API_HOST}/inputs?${params}`,
        {
          method: "GET",
          headers: AuthService.siteletHeaders,
          credentials: "include",
          signal,
        },
      );
      if (r.ok) {
        const resp = await r.json();

        if (resp?.errors) {
          Toaster.makeToast("Error on query", "error");
        }
        return resp?.results as {
          count: number;
          data: T[];
          facets: Record<string, FacetFilter[]>;
        };
      } else if (r.status === 401) {
        this.UserReset();
      }
      return {
        count: 0,
        data: [],
        facets: undefined,
      };
    },
  };
  public readonly inputService = {
    templates: async (input_id: string) => {
      const r = await fetch(
        `${process.env.REACT_APP_API_HOST}/inputs/${input_id}/templates`,
        {
          method: "GET",
          headers: AuthService.siteletHeaders,
          credentials: "include",
        },
      );
      if (r.ok) {
        const resp = await r.json();

        if (resp?.errors) {
          Toaster.makeToast("Error on query", "error");
        }
        return resp?.rest as {
          count: number;
          data: Template[];
        };
      } else if (r.status === 401) {
        this.UserReset();
      }
      return {
        count: 0,
        data: [],
      };
    },
    template: async (input_id: string, guid: UUID) => {
      const r = await fetch(
        `${process.env.REACT_APP_API_HOST}/inputs/${input_id}/templates/${guid}`,
        {
          method: "GET",
          headers: AuthService.siteletHeaders,
          credentials: "include",
        },
      );
      if (r.ok) {
        return r.blob();
      } else if (r.status === 401) {
        this.UserReset();
      }
      return null;
    },
    bulk_boundaries: async (input_ids: string[]) => {
      const r = await fetch(
        `${process.env.REACT_APP_API_HOST}/inputs/boundaries?input_ids=${input_ids}`,
        {
          method: "GET",
          headers: AuthService.siteletHeaders,
          credentials: "include",
        },
      );
      if (r.ok) {
        const resp = await r.json();

        if (resp?.errors) {
          Toaster.makeToast("Error on query", "error");
        }
        return resp as Record<string, Boundary[]>;
      } else if (r.status === 401) {
        this.UserReset();
        return {};
      }
    },
    boundaries: async (input_id: string, search_options?: SearchOptions) => {
      const params = new URLSearchParams({
        search_term: search_options?.search_term ?? "",
        page_num: search_options?.page_num.toString() ?? "",
        page_rows: search_options?.page_rows.toString() ?? "",
        order_by_field: search_options?.order_by?.column_name ?? "",
        order_by_order: search_options?.order_by?.order ?? "",
      }).toString();
      const r = await fetch(
        `${process.env.REACT_APP_API_HOST}/inputs/${input_id}/boundaries?${params}`,
        {
          method: "GET",
          headers: AuthService.siteletHeaders,
          credentials: "include",
        },
      );
      if (r.ok) {
        const resp = await r.json();

        if (resp?.errors) {
          Toaster.makeToast("Error on query", "error");
        }
        return resp?.rest as {
          count: number;
          data: Boundary[];
        };
      } else if (r.status === 401) {
        this.UserReset();
      }
      return {
        count: 0,
        data: [],
      };
    },
    bulk_points: async (input_ids: string[]) => {
      const r = await fetch(
        `${process.env.REACT_APP_API_HOST}/inputs/points?input_ids=${input_ids}`,
        {
          method: "GET",
          headers: AuthService.siteletHeaders,
          credentials: "include",
        },
      );
      if (r.ok) {
        const resp = await r.json();

        if (resp?.errors) {
          Toaster.makeToast("Error on query", "error");
        }
        return resp as Record<string, Point[]>;
      } else if (r.status === 401) {
        this.UserReset();
      }
      return {};
    },
    points: async (input_id: string, search_options?: SearchOptions) => {
      const params = new URLSearchParams({
        search_term: search_options?.search_term ?? "",
        page_num: search_options?.page_num.toString() ?? "",
        page_rows: search_options?.page_rows.toString() ?? "",
        order_by_field: search_options?.order_by?.column_name ?? "",
        order_by_order: search_options?.order_by?.order ?? "",
      }).toString();
      const r = await fetch(
        `${process.env.REACT_APP_API_HOST}/inputs/${input_id}/points?${params}`,
        {
          method: "GET",
          headers: AuthService.siteletHeaders,
          credentials: "include",
        },
      );
      if (r.ok) {
        const resp = await r.json();

        if (resp?.errors) {
          Toaster.makeToast("Error on query", "error");
        }
        return resp?.rest as {
          count: number;
          data: Point[];
        };
      } else if (r.status === 401) {
        this.UserReset();
      }
      return {
        count: 0,
        data: [],
      };
    },
    bulk_assets: async (input_ids: string[]) => {
      const r = await fetch(
        `${process.env.REACT_APP_API_HOST}/inputs/assets?input_ids=${input_ids}`,
        {
          method: "GET",
          headers: AuthService.siteletHeaders,
          credentials: "include",
        },
      );
      if (r.ok) {
        const resp = await r.json();

        if (resp?.errors) {
          Toaster.makeToast("Error on query", "error");
        }
        return resp as Record<string, Asset[]>;
      } else if (r.status === 401) {
        this.UserReset();
      }
      return {};
    },
    assets: async (input_id: string, search_options?: SearchOptions) => {
      const params = new URLSearchParams({
        search_term: search_options?.search_term ?? "",
        page_num: search_options?.page_num.toString() ?? "",
        page_rows: search_options?.page_rows.toString() ?? "",
        order_by_field: search_options?.order_by?.column_name ?? "",
        order_by_order: search_options?.order_by?.order ?? "",
      }).toString();
      const r = await fetch(
        `${process.env.REACT_APP_API_HOST}/inputs/${input_id}/assets?${params}`,
        {
          method: "GET",
          headers: AuthService.siteletHeaders,
          credentials: "include",
        },
      );
      if (r.ok) {
        const resp = await r.json();

        if (resp?.errors) {
          Toaster.makeToast("Error on query", "error");
        }
        return resp.r as {
          count: number;
          data: Asset[];
        };
      } else if (r.status === 401) {
        this.UserReset();
      }
      return {
        count: 0,
        data: [],
      };
    },
    get_related: async (input_id: string, search_options?: SearchOptions) => {
      const params = new URLSearchParams({
        search_term: search_options?.search_term ?? "",
        page_num: search_options?.page_num.toString() ?? "",
        page_rows: search_options?.page_rows.toString() ?? "",
        order_by_field: search_options?.order_by?.column_name ?? "",
        order_by_order: search_options?.order_by?.order ?? "",
      }).toString();
      const r = await fetch(
        `${process.env.REACT_APP_API_HOST}/inputs/${input_id}/related?${params}`,
        {
          method: "GET",
          headers: AuthService.siteletHeaders,
          credentials: "include",
        },
      );
      if (r.ok) {
        const resp = await r.json();

        if (resp?.errors) {
          Toaster.makeToast("Error on query", "error");
        }
        return resp?.results as {
          count: number;
          data: RelatedInput[];
        };
      } else if (r.status === 401) {
        this.UserReset();
      }
      return {
        count: 0,
        data: [],
      };
    },
  };

  private readonly UserReset = () => {
    AuthService.CurrentUser = undefined;
    Toaster.makeToast("User is not authorized!", "error");
  };
}
